/**
 * La card proposta: l'unità di risposta di Otto (§1.ter, P0 numeri 1 e 2).
 *
 * Non è testo formattato: è un componente che legge dallo stato condiviso e
 * **evolve in-place** — proposta → approvata → in esecuzione → ricevuta con le
 * spunte dei canali che si accendono. L'host vede accadere la modifica nello
 * stesso punto in cui l'ha approvata, e il tasto "Annulla" è lì, non nascosto
 * in una schermata di storico.
 */

import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Ban,
  Check,
  Info,
  RotateCcw,
  X,
} from "lucide-react";
import {
  formatEurDelta,
  formatNights,
  type ActionPreview,
  type ActionView,
  type PreviewWarning,
} from "@otto/shared";
import { api } from "../lib/api.ts";
import { messaggioDi } from "../lib/errori.ts";
import { useStore } from "../lib/store.tsx";
import { Card, Pill, StatusDot, Thumb, cx } from "../ui/primitives.tsx";
import { Receipt } from "./Receipt.tsx";

export function ProposalCard({
  actionId,
  fallback,
}: {
  actionId: string;
  fallback: ActionPreview;
}) {
  const store = useStore();
  const action = store.getAction(actionId);
  const preview = action?.preview ?? fallback;
  const blocked = preview.warnings.some((w) => w.severity === "blocco");

  /**
   * Lo store conosce solo le azioni recenti.
   *
   * Una card dentro una conversazione di due settimane fa non trovava la sua,
   * e mostrava lo stato di partenza: "Approva" su una modifica già eseguita —
   * cioè il tasto che promette di fare una cosa che è già stata fatta. Se
   * manca, si va a prenderla: una richiesta sola, e solo per le card vecchie.
   */
  const chiesta = useRef(false);
  const [risolta, setRisolta] = useState(false);

  useEffect(() => {
    if (action) {
      setRisolta(true);
      return;
    }
    if (chiesta.current) return;
    chiesta.current = true;
    void store.refreshAction(actionId).finally(() => setRisolta(true));
  }, [action, actionId, store]);

  // Finché non si sa, non si dice: mostrare "Approva" per un istante e poi
  // "Fatto" è peggio che aspettare mezzo secondo. Se l'azione non si trova
  // nemmeno sul server — cancellata, o di un altro account — si ricade sulla
  // proposta, che è quello che l'anteprima in mano descrive.
  const status = action?.status ?? (risolta ? "proposta" : null);

  /**
   * Lo stop generale è acceso: approvare non si può, e il tasto non deve
   * fingere. Il rifiuto lato server c'è comunque — è lì che conta — ma un
   * bottone che porta a un errore è un bottone che non andava mostrato.
   */
  const fermo = store.account?.killSwitch === true;

  const [busy, setBusy] = useState<"approve" | "reject" | "rollback" | null>(
    null,
  );

  const act = async (kind: "approve" | "reject" | "rollback") => {
    setBusy(kind);
    try {
      if (kind === "approve") await api.approve(actionId);
      if (kind === "reject") await api.reject(actionId);
      if (kind === "rollback") {
        await api.rollback(actionId);
        store.notify("Annullato. Ripristino i valori di prima.");
      }
      await store.refreshAction(actionId);
      await store.refreshAccount();
    } catch (error) {
      store.notify(messaggioDi(error));
      await store.refreshAction(actionId);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card
      size="lg"
      className={cx(
        "animate-rise overflow-hidden",
        blocked && "ring-1 ring-danger/25",
      )}
    >
      <div className="flex flex-col gap-3.5 p-4">
        <Header status={status} blocked={blocked} summary={preview.summary} />

        {preview.targets.length > 0 ? (
          <div className="flex flex-col gap-2.5">
            {preview.targets.map((target) => (
              <div
                key={target.listingId}
                className="flex gap-3 rounded-card bg-surface-muted/70 p-3"
              >
                <Thumb src={target.photoUrl} alt={target.nickname} size={40} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-ink">
                    {target.nickname}
                  </p>
                  {target.city ? (
                    <p className="text-[12px] text-ink-faint">{target.city}</p>
                  ) : null}

                  <div className="mt-2 flex flex-col gap-1.5">
                    {target.lines.map((line, index) => (
                      <div
                        key={`${line.field}-${index}`}
                        className="text-[13px]"
                      >
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                          <span className="text-ink-faint">{line.label}</span>
                          {line.from ? (
                            <>
                              <span className="text-ink-soft line-through decoration-ink-faint/40">
                                {line.from}
                              </span>
                              <ArrowRight
                                size={12}
                                className="text-ink-faint"
                              />
                            </>
                          ) : null}
                          <span className="font-bold text-ink">{line.to}</span>
                        </div>
                        {line.nights ? (
                          <p className="text-[12px] text-ink-faint">
                            {formatNights(line.nights)}
                          </p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        <Totals totals={preview.totals} />

        {preview.warnings.length > 0 ? (
          <div className="flex flex-col gap-2">
            {preview.warnings.map((warning, index) => (
              <Warning key={index} warning={warning} />
            ))}
          </div>
        ) : null}

        {action?.receipt ? (
          <div className="rounded-card bg-surface-muted/70 p-3">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-ink-faint">
              Ricevuta di sincronizzazione
            </p>
            <Receipt receipt={action.receipt} />
          </div>
        ) : null}

        {action?.errorMessage && !action.receipt ? (
          <p className="rounded-card bg-danger-soft px-3 py-2 text-[13px] leading-relaxed text-danger">
            {action.errorMessage}
          </p>
        ) : null}

        <Actions
          status={status}
          blocked={blocked}
          fermo={fermo}
          busy={busy}
          canRollback={action?.canRollback ?? false}
          etichetta={action?.kind === "messaggio_ospite" ? "Invia" : "Approva"}
          onAct={act}
        />
      </div>
    </Card>
  );
}

// ── Pezzi ───────────────────────────────────────────────────────────────────

function Header({
  status,
  blocked,
  summary,
}: {
  status: ActionView["status"] | null;
  blocked: boolean;
  summary: string;
}) {
  const state = describeStatus(status, blocked);

  return (
    <div className="flex items-start gap-2.5">
      <StatusDot tone={state.tone} className="mt-2" />
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-bold leading-snug text-ink">{summary}</p>
        {/* Lo stato cambia da solo mentre Otto scrive: chi usa uno screen
            reader deve sentirlo, non scoprirlo rileggendo la card. */}
        <p
          aria-live="polite"
          className={cx("mt-0.5 text-[12px] font-semibold", state.className)}
        >
          {state.label}
        </p>
      </div>
    </div>
  );
}

function Totals({ totals }: { totals: ActionPreview["totals"] }) {
  const parts: string[] = [];
  if (totals.listings > 1) parts.push(`${totals.listings} immobili`);
  if (totals.nights) parts.push(formatNights(totals.nights));

  if (parts.length === 0 && !totals.revenueDeltaCents) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
      {parts.length > 0 ? (
        <span className="text-ink-soft">{parts.join(" · ")}</span>
      ) : null}
      {totals.revenueDeltaCents ? (
        <span
          className={cx(
            "font-bold tabular-nums",
            totals.revenueDeltaCents > 0 ? "text-ok" : "text-ink-soft",
          )}
        >
          {formatEurDelta(totals.revenueDeltaCents)} stimati
        </span>
      ) : null}
    </div>
  );
}

function Warning({ warning }: { warning: PreviewWarning }) {
  const config = {
    blocco: { Icon: Ban, wrap: "bg-danger-soft", text: "text-danger" },
    attenzione: {
      Icon: AlertTriangle,
      wrap: "bg-warn-soft",
      text: "text-warn",
    },
    info: { Icon: Info, wrap: "bg-surface-muted", text: "text-ink-soft" },
  }[warning.severity];

  return (
    <div className={cx("flex gap-2.5 rounded-card px-3 py-2.5", config.wrap)}>
      <config.Icon size={15} className={cx("mt-0.5 shrink-0", config.text)} />
      <p className={cx("text-[13px] leading-relaxed", config.text)}>
        {warning.message}
      </p>
    </div>
  );
}

function Actions({
  status,
  blocked,
  fermo,
  busy,
  canRollback,
  etichetta,
  onAct,
}: {
  status: ActionView["status"] | null;
  /** «Invia» per una risposta all'ospite: è quello che succede col tap. */
  etichetta: string;
  blocked: boolean;
  fermo: boolean;
  busy: "approve" | "reject" | "rollback" | null;
  canRollback: boolean;
  onAct: (kind: "approve" | "reject" | "rollback") => void;
}) {
  // Ancora non si sa che fine ha fatto: meglio niente che il tasto sbagliato.
  if (status === null) return null;

  if (status === "proposta") {
    if (blocked) {
      return (
        <Pill
          variant="ghost"
          size="sm"
          icon={<X size={14} />}
          onClick={() => onAct("reject")}
          loading={busy === "reject"}
        >
          Ho capito, lascia perdere
        </Pill>
      );
    }
    return (
      <div className="flex flex-col gap-2">
        {fermo ? (
          <p className="rounded-card bg-warn-soft px-3 py-2 text-[12.5px] leading-relaxed text-warn">
            Lo stop generale è acceso: non scrivo niente sul gestionale. Per
            approvare, riattiva le automazioni dalle impostazioni.
          </p>
        ) : null}
        <div className="flex gap-2">
          <Pill
            variant="primary"
            icon={<Check size={16} strokeWidth={2.6} />}
            onClick={() => onAct("approve")}
            loading={busy === "approve"}
            disabled={fermo}
            title={
              fermo
                ? "Le automazioni sono ferme: riattivale dalle impostazioni."
                : undefined
            }
            className="flex-1"
          >
            {etichetta}
          </Pill>
          <Pill
            variant="ghost"
            onClick={() => onAct("reject")}
            loading={busy === "reject"}
          >
            Rifiuta
          </Pill>
        </div>
      </div>
    );
  }

  /**
   * A2: «Annulla» non è solo delle riuscite.
   *
   * Un'esecuzione che si ferma a metà — due immobili scritti su tre, o il
   * processo morto fra una scrittura e l'altra — lascia l'azione `fallita` e i
   * cambiamenti già arrivati sul gestionale. Legare il tasto a `eseguita`
   * voleva dire lasciarli lì finché l'host non entrava in Guesty a mano.
   * Chi decide se c'è qualcosa da annullare è il server (`isRollbackable`):
   * qui ci si fida di `canRollback` e basta.
   */
  if (canRollback) {
    const parziale = status === "fallita";
    return (
      <div className="flex flex-col items-start gap-2">
        {parziale ? (
          <p className="rounded-card bg-warn-soft px-3 py-2 text-[12.5px] leading-relaxed text-warn">
            La modifica si è fermata a metà: qualcosa è già arrivato sul
            gestionale. Se annulli, riporto indietro solo gli immobili su cui
            ero riuscito a scrivere — sugli altri non è cambiato niente.
          </p>
        ) : null}
        <Pill
          variant="ghost"
          size="sm"
          icon={<RotateCcw size={14} />}
          onClick={() => onAct("rollback")}
          loading={busy === "rollback"}
          className="self-start"
        >
          {parziale ? "Annulla quelli riusciti" : "Annulla"}
        </Pill>
      </div>
    );
  }

  return null;
}

function describeStatus(status: ActionView["status"] | null, blocked: boolean) {
  if (status === null) {
    return {
      tone: "idle" as const,
      label: "Sto controllando a che punto è…",
      className: "text-ink-faint",
    };
  }

  if (blocked && status === "proposta") {
    return {
      tone: "danger" as const,
      label: "Non posso farlo",
      className: "text-danger",
    };
  }

  switch (status) {
    case "proposta":
      return {
        tone: "idle" as const,
        label: "In attesa della tua conferma",
        className: "text-ink-faint",
      };
    case "approvata":
      return {
        tone: "wait" as const,
        label: "Approvata, la sto scrivendo…",
        className: "text-ink-soft",
      };
    case "in_esecuzione":
      return {
        tone: "wait" as const,
        label: "Sto scrivendo sul gestionale…",
        className: "text-ink-soft",
      };
    case "eseguita":
      return { tone: "ok" as const, label: "Fatto", className: "text-ok" };
    case "fallita":
      return {
        tone: "danger" as const,
        label: "Non è riuscita",
        className: "text-danger",
      };
    case "annullata":
      return {
        tone: "idle" as const,
        label: "Rifiutata",
        className: "text-ink-faint",
      };
    case "rollback":
      return {
        tone: "idle" as const,
        label: "Annullata",
        className: "text-ink-faint",
      };
  }
}
